// Inventario físico nativo (docs/108 B2). Quien CUENTA ve una hoja ciega (SKU, producto, unidad y su
// cantidad); quien CIERRA ve además el saldo del libro y la diferencia. Al cerrar, cada diferencia
// queda como un ajuste en el libro (append-only), y la hoja ya no cambia.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Grilla, type Columna } from '../comun/Grilla';
import { Modal } from '../comun/Modal';
import { decimal, fecha, fechaHora } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import {
  clavesStock,
  useConteo,
  useConteos,
  useResumenProductos,
  type ConteoResumen,
  type LineaConteo,
} from './datos';
import { SelectorBodega, useBodegas } from './PantallasStock';
import { coincide } from '../comun/coincide';

const ESTADO: Record<ConteoResumen['estado'], string> = {
  ABIERTO: 'Abierta',
  CERRADO: 'Cerrada',
  ANULADO: 'Anulada',
};

export function InventarioFisico() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const hojas = useConteos(empresaId);
  const bodegas = useBodegas(empresaId);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);
  const codigo = new Map(bodegas.lista.map((b) => [b.id, `${b.codigo} · ${b.nombre}`]));
  const columnas: Columna<ConteoResumen>[] = [
    {
      id: 'n',
      titulo: 'N°',
      celda: (c) => <span className="mono">{c.numero}</span>,
      ancho: '4rem',
    },
    { id: 'fecha', titulo: 'Fecha', celda: (c) => fecha(c.fechaNegocio), ancho: '7rem' },
    { id: 'bodega', titulo: 'Bodega', celda: (c) => codigo.get(c.ubicacionId) ?? '—' },
    { id: 'motivo', titulo: 'Motivo', celda: (c) => c.motivo },
    {
      id: 'avance',
      titulo: 'Contados',
      celda: (c) => `${c.contadas} / ${c.lineas}`,
      alinear: 'der',
      ancho: '7rem',
    },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (c) => (
        <span className={`insignia ${c.estado === 'ABIERTO' ? 'tono-aviso' : 'tono-neutro'}`}>
          {ESTADO[c.estado]}
        </span>
      ),
      ancho: '7rem',
    },
    {
      id: 'cerrada',
      titulo: 'Cerrada',
      celda: (c) => (c.cerradoEn ? fechaHora(c.cerradoEn) : '—'),
      ancho: '9rem',
    },
  ];
  return (
    <div className="pantalla consulta" data-pantalla="inventario-fisico">
      {abierta ? (
        <HojaConteo empresaId={empresaId} conteoId={abierta} alVolver={() => setAbierta(null)} />
      ) : (
        <>
          <div className="barra-filtros">
            <span className="apagado">
              Una hoja por bodega a la vez. Se cuenta a ciegas; al cerrar, las diferencias se
              registran como ajustes de stock.
            </span>
            <span className="espaciador" />
            {puede('inventario.conteo.abrir') && (
              <button type="button" className="primario" onClick={() => setNueva(true)}>
                Nueva hoja de inventario
              </button>
            )}
          </div>
          {hojas.isError && <PanelError error={hojas.error} />}
          <Grilla
            etiqueta="Hojas de inventario físico"
            columnas={columnas}
            filas={hojas.data ?? []}
            clave={(c) => c.id}
            cargando={hojas.isLoading}
            vacio="Todavía no hay hojas de inventario"
            alAbrir={(c) => setAbierta(c.id)}
          />
        </>
      )}
      {nueva && (
        <NuevaHoja
          empresaId={empresaId}
          alCerrar={() => setNueva(false)}
          alCrear={(id) => {
            setNueva(false);
            setAbierta(id);
          }}
        />
      )}
    </div>
  );
}

function NuevaHoja({
  empresaId,
  alCerrar,
  alCrear,
}: {
  empresaId: string;
  alCerrar: () => void;
  alCrear: (id: string) => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const bodegas = useBodegas(empresaId);
  const [elegida, setElegida] = useState('');
  const ubicacionId = elegida || bodegas.lista[0]?.id || '';
  const [motivo, setMotivo] = useState('Inventario físico');
  const abrir = useMutation({
    mutationFn: () => {
      const cuerpo = { empresaId, ubicacionId, motivo: motivo.trim() };
      return api.post<{ conteoId: string; numero: number; lineas: number }>(
        '/inventario/conteos',
        cuerpo,
        { idempotencia: intentos.clave('abrir-conteo', cuerpo) },
      );
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: clavesStock.stock(empresaId) });
      alCrear(r.conteoId);
    },
    onError: (e) => intentos.cerrar(e),
  });
  return (
    <Modal titulo="Nueva hoja de inventario físico" alCerrar={alCerrar}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (motivo.trim().length >= 5 && ubicacionId) abrir.mutate();
        }}
      >
        <p className="apagado">
          La hoja incluye todos los productos con saldo en la bodega. Mientras esté abierta, nadie
          puede abrir otra en la misma bodega.
        </p>
        <SelectorBodega bodegas={bodegas.lista} valor={ubicacionId} alCambiar={setElegida} />
        <label className="campo">
          <span className="etiqueta">Motivo</span>
          <input aria-label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        {abrir.isError && <PanelError error={abrir.error} />}
        <div className="acciones-modal">
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="primario"
            disabled={abrir.isPending || motivo.trim().length < 5}
          >
            Abrir hoja
          </button>
        </div>
      </form>
    </Modal>
  );
}

function HojaConteo({
  empresaId,
  conteoId,
  alVolver,
}: {
  empresaId: string;
  conteoId: string;
  alVolver: () => void;
}) {
  const { puede } = useSesion();
  const qc = useQueryClient();
  const intentos = useIntentos();
  const hoja = useConteo(empresaId, conteoId);
  const lineas = hoja.data?.lineasDetalle ?? [];
  const productos = useResumenProductos(
    empresaId,
    lineas.map((l) => l.productoId),
  );
  const [cambios, setCambios] = useState<Record<string, string>>({});
  const [texto, setTexto] = useState('');
  const [cerrando, setCerrando] = useState<'cerrar' | 'anular' | null>(null);
  const [motivo, setMotivo] = useState('');
  const abierta = hoja.data?.estado === 'ABIERTO';
  const revisa = puede('inventario.conteo.cerrar');
  const puedeContar = abierta && puede('inventario.conteo.contar');
  const refrescar = () => qc.invalidateQueries({ queryKey: clavesStock.stock(empresaId) });

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        version: hoja.data!.version,
        lineas: Object.entries(cambios).map(([productoId, contado]) => ({
          productoId,
          contado: contado.replace(',', '.'),
        })),
      };
      return api.post(`/inventario/conteos/${conteoId}/contar`, cuerpo);
    },
    onSuccess: async () => {
      setCambios({});
      await refrescar();
    },
  });
  const finalizar = useMutation({
    mutationFn: (accion: 'cerrar' | 'anular') => {
      const cuerpo = { empresaId, version: hoja.data!.version, motivo: motivo.trim() };
      return api.post<{ ajustes?: number }>(`/inventario/conteos/${conteoId}/${accion}`, cuerpo, {
        idempotencia: intentos.clave(`conteo-${accion}`, cuerpo),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      setCerrando(null);
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });

  const invalidas = Object.values(cambios).filter(
    (v) => v.trim() !== '' && !/^\d+([.,]\d{1,4})?$/.test(v.trim()),
  ).length;
  const t = texto.trim().toLowerCase();
  const visibles = lineas
    .filter((l) => {
      if (!t) return true;
      const p = productos.mapa.get(l.productoId);
      return !!p && coincide(t, p.sku, p.nombre);
    })
    .sort((a, b) =>
      (productos.mapa.get(a.productoId)?.nombre ?? '').localeCompare(
        productos.mapa.get(b.productoId)?.nombre ?? '',
      ),
    );
  const columnas: Columna<LineaConteo>[] = [
    {
      id: 'sku',
      titulo: 'SKU',
      celda: (l) => <span className="mono">{productos.mapa.get(l.productoId)?.sku ?? '…'}</span>,
      ancho: '8rem',
    },
    {
      id: 'producto',
      titulo: 'Producto',
      celda: (l) => productos.mapa.get(l.productoId)?.nombre ?? '…',
    },
    {
      id: 'unidad',
      titulo: 'Unidad',
      celda: (l) => productos.mapa.get(l.productoId)?.unidadBase ?? '',
      ancho: '5rem',
    },
    {
      id: 'contado',
      titulo: 'Contado',
      celda: (l) =>
        puedeContar ? (
          <input
            className="entrada-numero"
            aria-label={`Contado ${productos.mapa.get(l.productoId)?.sku ?? ''}`}
            inputMode="decimal"
            value={cambios[l.productoId] ?? (l.contado === null ? '' : decimal(l.contado))}
            onChange={(e) => setCambios((c) => ({ ...c, [l.productoId]: e.target.value }))}
          />
        ) : l.contado === null ? (
          <span className="apagado">sin contar</span>
        ) : (
          decimal(l.contado)
        ),
      alinear: 'der',
      ancho: '8rem',
    },
    ...(revisa || !abierta
      ? ([
          {
            id: 'libro',
            titulo: abierta ? 'Saldo libro (al abrir)' : 'Saldo libro (al cerrar)',
            celda: (l: LineaConteo) =>
              decimal(abierta ? l.saldoAlAbrir : (l.saldoAlCerrar ?? l.saldoAlAbrir)),
            alinear: 'der',
            ancho: '9rem',
          },
          {
            id: 'dif',
            titulo: 'Diferencia',
            celda: (l: LineaConteo) => {
              const d = abierta
                ? l.contado === null
                  ? null
                  : Number(l.contado) - Number(l.saldoAlAbrir)
                : l.diferencia === null
                  ? null
                  : Number(l.diferencia);
              if (d === null) return '';
              return (
                <b className={d < 0 ? 'tono-malo' : d > 0 ? 'tono-aviso' : ''}>
                  {d > 0 ? '+' : ''}
                  {decimal(String(d))}
                </b>
              );
            },
            alinear: 'der',
            ancho: '8rem',
          },
        ] as Columna<LineaConteo>[])
      : []),
  ];
  const sinGuardar = Object.keys(cambios).length;
  return (
    <>
      <div className="barra-filtros">
        <button type="button" onClick={alVolver}>
          ← Hojas
        </button>
        <h2 className="titulo-hoja">
          Inventario N° {hoja.data?.numero ?? '…'}{' '}
          {hoja.data && <span className="insignia tono-neutro">{ESTADO[hoja.data.estado]}</span>}
        </h2>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar en la hoja"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        {hoja.data && (
          <span className="apagado">
            {hoja.data.contadas} de {hoja.data.lineas} contados
          </span>
        )}
        {puedeContar && (
          <button
            type="button"
            className="primario"
            disabled={!sinGuardar || !!invalidas || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            Guardar conteo{sinGuardar ? ` (${sinGuardar})` : ''}
          </button>
        )}
        {abierta && revisa && (
          <>
            <button type="button" disabled={!!sinGuardar} onClick={() => setCerrando('cerrar')}>
              Cerrar y ajustar
            </button>
            <button type="button" onClick={() => setCerrando('anular')}>
              Anular
            </button>
          </>
        )}
      </div>
      {!revisa && abierta && (
        <p className="aviso-linea tono-neutro">
          Conteo ciego: cuenta lo que hay físicamente; el sistema compara al cerrar.
        </p>
      )}
      {hoja.isError && <PanelError error={hoja.error} />}
      {guardar.isError && <PanelError error={guardar.error} />}
      {!!invalidas && (
        <p className="aviso-linea tono-malo" role="alert">
          Hay {invalidas} cantidad(es) que no son números válidos.
        </p>
      )}
      <Grilla
        etiqueta="Líneas de la hoja"
        columnas={columnas}
        filas={visibles}
        clave={(l) => l.productoId}
        cargando={hoja.isLoading}
        vacio="Sin productos"
      />
      {cerrando && (
        <Modal
          titulo={cerrando === 'cerrar' ? 'Cerrar la hoja y ajustar' : 'Anular la hoja'}
          alCerrar={() => setCerrando(null)}
        >
          <p>
            {cerrando === 'cerrar'
              ? 'Cada producto contado se compara con el saldo del libro AHORA; la diferencia queda como un ajuste. Lo que no contaste no se ajusta. La hoja ya no se podrá cambiar.'
              : 'La hoja queda anulada sin ajustar nada y la bodega queda libre para otra hoja.'}
          </p>
          <label className="campo">
            <span className="etiqueta">Motivo (obligatorio)</span>
            <input
              aria-label="Motivo del cierre"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {finalizar.isError && <PanelError error={finalizar.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setCerrando(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={motivo.trim().length < 5 || finalizar.isPending}
              onClick={() => finalizar.mutate(cerrando)}
            >
              {cerrando === 'cerrar' ? 'Cerrar y ajustar' : 'Anular hoja'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
